/**
 * «ЗП»: из чего сложилась зарплата неделя за неделей. Спека
 * `docs/specs/2026-10-07-salary-design.md`; числа — те же, что у
 * `prokat-runner salary-weeks`, из одного модуля на сервере.
 *
 * Колонка — неделя (пн–вс), строка — показатель: начислено, часы акцепта,
 * затем разделы «Часы», «Гарантия и доплаты», «Премии» до каждого начисления
 * биллинга. Своя зарплата — любому вошедшему, чужая — по праву `salary_all`.
 * Вкладка «Ставка в час» (`?view=rate`, `SalaryRate.tsx`) — эффективная ставка
 * по периодам из 4 недель; с правом — весь отдел или `?users=525,358`.
 */
import { useGetIdentity } from "@refinedev/core";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Fragment, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router";

import { Combo } from "@/components/Combo";
import { BONUS_ALL, has, SALARY_ALL } from "@/lib/access";
import { KC, money } from "@/lib/bonus";
import { iso, isValidDate, plusDays } from "@/lib/dates";
import type { CurrentUser } from "@/lib/providers";
import {
  DEFAULT_RATE_WEEKS,
  DEFAULT_WEEKS,
  loadSalary,
  loadSalaryRate,
  RATE_WEEKS,
  type Salary,
  type SalaryWeek,
  WEEKS,
} from "@/lib/salary";
import { RateReport } from "@/pages/SalaryRate";

type View = "weeks" | "rate";
type Params = { view: View; user: number | null; users: number[]; weeks: number; to: string | null; location: number };

const VIEWS: { id: View; label: string; hint: string }[] = [
  { id: "weeks", label: "По неделям", hint: "из чего сложилась" },
  { id: "rate", label: "Ставка в час", hint: "сколько за час и как менялась" },
];

const dm = (day: string) => `${day.slice(8, 10)}.${day.slice(5, 7)}`;
const hm = (minutes: number) => `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, "0")}`;
const WEEKDAYS = ["вс", "пн", "вт", "ср", "чт", "пт", "сб"];
const weekday = (day: string) => WEEKDAYS[new Date(`${day}T00:00:00`).getDay()];

/** Сотрудник, число недель, последняя неделя и подразделение из адреса: ссылку можно переслать. */
function useSalaryParams() {
  const [params, setParams] = useSearchParams();
  const to = params.get("to");
  const view: View = params.get("view") === "rate" ? "rate" : "weeks";
  const [choice, fallback] = view === "rate" ? [RATE_WEEKS, DEFAULT_RATE_WEEKS] : [WEEKS, DEFAULT_WEEKS];
  const p: Params = {
    view,
    user: Number(params.get("user")) || null,
    users: (params.get("users") ?? "").split(",").map(Number).filter(Boolean),
    weeks: choice.includes(Number(params.get("weeks"))) ? Number(params.get("weeks")) : fallback,
    to: to && isValidDate(to) ? to : null,
    location: Number(params.get("location")) || KC,
  };
  const set = (next: Partial<Params>) => {
    const v = { ...p, ...next };
    const out: Record<string, string> = {};
    if (v.view !== "weeks") out["view"] = v.view;
    if (v.user) out["user"] = String(v.user);
    if (v.users.length) out["users"] = v.users.join(",");
    if (v.weeks !== (v.view === "rate" ? DEFAULT_RATE_WEEKS : DEFAULT_WEEKS)) out["weeks"] = String(v.weeks);
    if (v.to) out["to"] = v.to;
    if (v.location !== KC) out["location"] = String(v.location);
    setParams(out, { replace: true });
  };
  return [p, set] as const;
}

function Amount({ v, strong = false }: { v: number | null; strong?: boolean }) {
  if (v === null || v === 0) return <span className="text-fg-faint">·</span>;
  return <span className={`tabular-nums ${strong ? "font-semibold" : ""} ${v < 0 ? "text-err" : ""}`}>{money(v)}</span>;
}

/** Разница с прошлой неделей: рост — зелёным, падение — красным. */
function Delta({ now, was, unit = "" }: { now: number; was: number | undefined; unit?: string }) {
  if (was === undefined || now === was) return null;
  const d = now - was;
  return (
    <span className={`block text-[11px] tabular-nums ${d > 0 ? "text-ok" : "text-err"}`} title="к предыдущей неделе">
      {d > 0 ? "+" : "−"}
      {unit === "ч" ? hm(Math.abs(d)) : money(Math.abs(d))}
    </span>
  );
}

const cell = "whitespace-nowrap px-3 py-1.5 text-right align-top";
const first = "sticky left-0 z-10 bg-bg-subtle px-4 py-1.5 text-left align-top";

function Row({ label, hint, children, tone = "" }: { label: ReactNode; hint?: string | null; children: ReactNode; tone?: string }) {
  return (
    <tr className={`border-t border-border ${tone}`}>
      <th scope="row" className={`${first} font-normal`}>
        {label}
        {hint && <span className="block max-w-[340px] whitespace-normal text-[11.5px] text-fg-faint">{hint}</span>}
      </th>
      {children}
    </tr>
  );
}

function Report({ r, bonusLink }: { r: Salary; bonusLink: ((w: SalaryWeek) => string) | null }) {
  const weeks = r.weeks;
  const max = Math.max(1, ...weeks.map((w) => w.total));
  const filled = r.sections.filter((s) => s.categories.length > 0);
  return (
    <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle" data-annot="зарплата по неделям">
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead className="text-[11.5px] text-fg-muted">
            <tr>
              <th className={`${first} font-medium`}>Неделя</th>
              {weeks.map((w) => (
                <th key={w.from} className={`${cell} font-medium`} data-annot={`неделя ${w.week}`}>
                  <span className="text-fg">№ {w.week}</span>
                  <span className="block font-normal">
                    {dm(w.from)} — {dm(w.to)}
                  </span>
                  {w.open && (
                    <span className="block font-normal text-warn" title="Часы и гарантию CRM начислит в понедельник">
                      идёт
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <Row label={<span className="text-[13px] font-semibold">Начислено</span>} hint="часы + гарантия и доплаты + премии">
              {weeks.map((w, i) => (
                <td key={w.from} className={`${cell} text-[14px]`} data-annot={`начислено за неделю ${w.week}`}>
                  <Amount v={w.total} strong />
                  <Delta now={w.total} was={weeks[i - 1]?.total} />
                  <span className="ml-auto mt-1 block h-1 w-20 rounded bg-neutral-bg">
                    <span className="ml-auto block h-1 rounded bg-accent" style={{ width: `${Math.max(0, (w.total / max) * 100)}%` }} />
                  </span>
                </td>
              ))}
            </Row>
            <Row label="Часов в акцепте" hint="подтверждённое рабочее время — по нему платят часы">
              {weeks.map((w, i) => (
                <td key={w.from} className={cell}>
                  <span className="tabular-nums">{w.minutes ? hm(w.minutes) : <span className="text-fg-faint">·</span>}</span>
                  <Delta now={w.minutes} was={weeks[i - 1]?.minutes} unit="ч" />
                </td>
              ))}
            </Row>
            <Row label="Смен" hint="дни с акцептом">
              {weeks.map((w) => {
                const days = w.days.filter((d) => d.minutes > 0);
                return (
                  <td key={w.from} className={cell} title={days.map((d) => `${weekday(d.date)} ${dm(d.date)} — ${hm(d.minutes)}`).join("\n")}>
                    <span className="tabular-nums">{w.shifts || <span className="text-fg-faint">·</span>}</span>
                    <span className="block text-[11px] text-fg-faint">{days.map((d) => weekday(d.date)).join(" ")}</span>
                  </td>
                );
              })}
            </Row>
            <Row label="В среднем за час" hint="начислено ÷ часы акцепта">
              {weeks.map((w) => (
                <td key={w.from} className={cell}>
                  <Amount v={w.per_hour} />
                </td>
              ))}
            </Row>
            {r.guarantee && (
              <Row
                label="Минимум по гарантии"
                hint={`часы акцепта × ${money(r.guarantee.price)} ₽ — меньше неделя не выйдет (при акцепте от ${money(r.guarantee.min_hours)} ч)`}
              >
                {weeks.map((w) => (
                  <td key={w.from} className={`${cell} text-fg-muted`}>
                    <Amount v={w.guarantee_floor} />
                  </td>
                ))}
              </Row>
            )}
            {filled.map((s) => (
              <Fragment key={s.id}>
                <tr className="border-t border-border-strong bg-bg" data-annot={`раздел «${s.name}»`}>
                  <th scope="row" className="sticky left-0 z-10 bg-bg px-4 py-2 text-left text-[13px] font-semibold">
                    {s.name}
                  </th>
                  {s.totals.map((v, i) => (
                    <td key={weeks[i]!.from} className={cell}>
                      <Amount v={v} strong />
                    </td>
                  ))}
                </tr>
                {s.categories.map((c) => (
                  <Fragment key={c.id}>
                    {s.id === "bonus" && (
                      <Row label={<span className="font-medium text-fg-muted">{c.name}</span>}>
                        {c.totals.map((v, i) => (
                          <td key={weeks[i]!.from} className={`${cell} text-fg-muted`}>
                            <Amount v={v} />
                          </td>
                        ))}
                      </Row>
                    )}
                    {c.modules.map((m) => (
                      <Row
                        key={m.id}
                        label={
                          <span className={s.id === "bonus" ? "pl-4" : ""}>
                            {m.name}
                            {m.manual && <span className="ml-2 text-[11.5px] text-fg-faint">вручную</span>}
                          </span>
                        }
                        hint={m.rule}
                      >
                        {m.totals.map((v, i) => {
                          const w = weeks[i]!;
                          const link = s.id === "bonus" && bonusLink && v !== 0 ? bonusLink(w) : null;
                          return (
                            <td key={w.from} className={cell} title={m.counts[i] ? `${m.counts[i]} шт` : undefined}>
                              {link ? (
                                <Link to={link} className="hover:underline" title={`${m.counts[i]} шт — открыть проводки недели в «Премии»`}>
                                  <Amount v={v} />
                                </Link>
                              ) : (
                                <Amount v={v} />
                              )}
                              {s.id === "bonus" && m.counts[i]! > 1 && <span className="block text-[11px] text-fg-faint">{m.counts[i]} шт</span>}
                            </td>
                          );
                        })}
                      </Row>
                    ))}
                  </Fragment>
                ))}
              </Fragment>
            ))}
            {weeks.some((w) => w.minus_left !== 0) && (
              <Row label="Не удержано" hint="вычеты больше премий недели: в ноль, глубже CRM не снимает">
                {weeks.map((w) => (
                  <td key={w.from} className={cell}>
                    <Amount v={w.minus_left ? -w.minus_left : 0} />
                  </td>
                ))}
              </Row>
            )}
            <tr className="border-t border-border-strong" data-annot="выплачено">
              <th scope="row" className={`${first} font-medium`}>
                Выплачено
                <span className="block max-w-[340px] whitespace-normal text-[11.5px] font-normal text-fg-faint">
                  заявка на выплату ЗП за неделю: начисленное с округлением и остатком прошлых недель
                </span>
              </th>
              {weeks.map((w) => (
                <td key={w.from} className={cell}>
                  <Amount v={w.paid} strong />
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}

function How({ r }: { r: Salary }) {
  return (
    <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-4 text-[12.5px] text-fg-muted" data-annot="как считается зарплата">
      <h2 className="text-[13px] font-semibold text-fg">Как считается неделя</h2>
      <ul className="mt-2 list-disc space-y-1 pl-5">
        <li>
          <b className="font-medium text-fg">Часы</b> — время в акцепте × часовая ставка
          {r.user.rate ? ` (сейчас ${money(r.user.rate)} ₽/ч)` : ""}. Платят все подтверждённые часы.
        </li>
        <li>
          <b className="font-medium text-fg">Премии</b> — начисления биллинга за звонки, записи, брони и прочее; у каждого своё правило, оно под названием.
        </li>
        {r.guarantee ? (
          <li>
            <b className="font-medium text-fg">Гарантия</b> — если часы и премии вместе дали меньше {money(r.guarantee.price)} ₽ за час акцепта, CRM
            доплачивает разницу. Поэтому неделя не бывает меньше «часы акцепта × {money(r.guarantee.price)} ₽», а больше — когда премий много.
          </li>
        ) : (
          <li>Гарантированной ставки у сотрудника нет.</li>
        )}
        <li>Неделя — с понедельника по воскресенье; часы и гарантию CRM начисляет в понедельник за прошлую неделю.</li>
      </ul>
    </section>
  );
}

export function SalaryPage() {
  const [p, set] = useSalaryParams();
  const { data: me } = useGetIdentity<CurrentUser>();
  const canAll = has(me, SALARY_ALL);
  const byRate = p.view === "rate";
  const weekly = useQuery({
    queryKey: ["salary", p.weeks, p.to, p.user, p.location],
    queryFn: () => loadSalary(p),
    enabled: !byRate,
  });
  const rate = useQuery({
    queryKey: ["salary-rate", p.weeks, p.to, p.users.join(","), p.location],
    queryFn: () => loadSalaryRate(p),
    enabled: byRate,
  });
  const q = byRate ? rate : weekly;
  const people = (byRate ? rate.data?.staff : weekly.data?.staff) ?? [];
  const locations = (byRate ? rate.data?.locations : weekly.data?.locations) ?? [];
  const mine = !p.user || p.user === me?.id;
  // Вошедший — первой строкой: он может не числиться в выбранном подразделении.
  const staff = [
    { value: "", label: `Я — ${me?.name ?? ""}` },
    ...people.filter((s) => s.id !== me?.id).map((s) => ({ value: String(s.id), label: s.name })),
  ];
  // Ставка: «весь отдел» по умолчанию, один сотрудник из выбора или несколько из ссылки.
  const rateStaff = [{ value: "", label: "Весь отдел" }, ...people.map((s) => ({ value: String(s.id), label: s.name }))];
  const named = (rate.data?.rows ?? []).filter((r) => p.users.includes(r.user.id)).map((r) => r.user.name);
  if (p.users.length > 1) rateStaff.splice(1, 0, { value: "many", label: named.join(", ") || "выбранные" });
  const last = byRate ? rate.data?.rows[0]?.periods.at(-1) : weekly.data?.weeks.at(-1);
  const shift = (weeks: number) => last && set({ to: iso(plusDays(new Date(`${last.to}T00:00:00`), 7 * weeks)) });
  // Проводки премий недели — на экране «Премия»: свои открыты всем, чужие — по его праву.
  const bonusLink =
    mine || has(me, BONUS_ALL)
      ? (w: SalaryWeek) => `/bonus?from=${w.from}&to=${w.to}${mine ? "" : `&user=${p.user}`}${p.location !== KC ? `&location=${p.location}` : ""}`
      : null;
  return (
    <main className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2" data-annot="шапка зарплаты">
        <h1 className="text-[16px] font-semibold tracking-[-0.015em]">
          {canAll ? "ЗП" : "Моя ЗП"}
          {!byRate && !mine && weekly.data && <span className="ml-2 font-normal text-fg-muted">{weekly.data.user.name}</span>}
        </h1>
        <div role="tablist" aria-label="Вид зарплаты" className="flex gap-2">
          {VIEWS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={p.view === t.id}
              onClick={() => set({ view: t.id, weeks: t.id === "rate" ? DEFAULT_RATE_WEEKS : DEFAULT_WEEKS, users: [] })}
              className={`flex items-baseline gap-2 rounded-[var(--radius-card)] border px-4 py-2 ${
                p.view === t.id ? "border-accent bg-accent-dim text-accent-text" : "border-border text-fg-muted hover:bg-bg-hover hover:text-fg"
              }`}
              data-annot={`вид «${t.label}»`}
            >
              <span className="text-[15px] font-semibold">{t.label}</span>
              <span className="text-[12px] opacity-80">{t.hint}</span>
            </button>
          ))}
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-3 text-[12.5px] text-fg-muted" data-annot="отбор зарплаты">
          {canAll && locations.length > 0 && (
            <Combo
              label="Отдел"
              value={String(p.location)}
              options={locations.map((l) => ({ value: String(l.id), label: l.name }))}
              onChange={(v) => set({ location: Number(v) || KC, user: null, users: [] })}
              width={200}
            />
          )}
          {canAll && byRate && (
            <Combo
              label="Сотрудник"
              value={p.users.length > 1 ? "many" : p.users.length === 1 ? String(p.users[0]) : ""}
              options={rateStaff}
              onChange={(v) => v !== "many" && set({ users: Number(v) ? [Number(v)] : [] })}
              placeholder="имя сотрудника"
              width={240}
            />
          )}
          {canAll && !byRate && (
            <Combo
              label="Сотрудник"
              value={mine ? "" : String(p.user)}
              options={staff}
              onChange={(v) => set({ user: Number(v) || null })}
              placeholder="имя сотрудника"
              width={240}
            />
          )}
          <div role="group" aria-label="Сколько недель" className="flex items-center gap-1" data-annot="сколько недель">
            <span className="mr-1">Недель</span>
            {(byRate ? RATE_WEEKS : WEEKS).map((n) => (
              <button
                key={n}
                type="button"
                aria-pressed={p.weeks === n}
                onClick={() => set({ weeks: n })}
                className={`h-7 rounded-[var(--radius-ui)] border px-2.5 tabular-nums ${
                  p.weeks === n ? "border-accent bg-accent-dim text-accent-text" : "border-border hover:bg-bg-hover hover:text-fg"
                }`}
              >
                {n}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1" data-annot="последняя неделя">
            <button type="button" title="На неделю раньше" onClick={() => shift(-1)} className="grid h-7 w-7 place-items-center rounded-[var(--radius-ui)] border border-border hover:bg-bg-hover">
              <ChevronLeft size={14} />
            </button>
            <span className="min-w-[92px] text-center tabular-nums">{last ? `по ${dm(last.to)}` : "…"}</span>
            <button type="button" title="На неделю позже" onClick={() => shift(1)} className="grid h-7 w-7 place-items-center rounded-[var(--radius-ui)] border border-border hover:bg-bg-hover">
              <ChevronRight size={14} />
            </button>
            {p.to && (
              <button type="button" onClick={() => set({ to: null })} className="h-7 rounded-[var(--radius-ui)] border border-border px-2.5 hover:bg-bg-hover hover:text-fg">
                прошлая неделя
              </button>
            )}
          </div>
        </div>
      </div>
      {q.isPending ? (
        <div className="text-[12.5px] text-fg-muted">Читаю биллинг CRM…</div>
      ) : q.isError ? (
        <div className="text-[12.5px] text-err">Не удалось получить зарплату: {String(q.error.message)}</div>
      ) : byRate ? (
        rate.data && <RateReport r={rate.data} openUser={canAll ? (id) => set({ view: "weeks", weeks: DEFAULT_WEEKS, user: id, users: [] }) : null} />
      ) : (
        weekly.data && (
          <>
            <Report r={weekly.data} bonusLink={bonusLink} />
            <How r={weekly.data} />
          </>
        )
      )}
    </main>
  );
}
